import { useContext, useEffect, useState } from "react";
import AuthContext from "../Context/AuthProvider";
import {
  addPrescription,
  getPrescription,
  removePrescription,
  updatePrescription,
  viewPrescriptionById,
} from "../API/PrescriptionApi";

import dayjs from "dayjs";
import { getServiceDetails } from "../API/LinkedServicesApi";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FormatLineSpacingOutlinedIcon from "@mui/icons-material/FormatLineSpacingOutlined";
import { sampleCollectionMethodOptions } from "../Utility/Constants";
import {
  samples,
  sampleTypes,
  sampleCollectionMethods,
} from "../Utility/Constants";
import {
  Button,
  Card,
  Grid2,
  Typography,
  Box,
  TextField,
  useMediaQuery,
  useTheme,
  TableCell,
  Table,
  TableRow,
  TableHead,
  TableBody,
  Autocomplete,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { useSnackbar } from "../Utility/UserMessageContext";
import { formatAge, showLoadingImage } from "../Utility/Utility";
import { convertUTCToTimeZoneDateTimeString } from "../Utility/DateTimeUtility";
import { IconButton } from "@mui/material";
import MenuBar from "../common/MenuBar";
import GenericTextField from "../common/GenericTextField";
import HeaderValueDisplay from "../common/HeaderValueDisplay";
import GenericDatePicker from "../common/GenericDatePicker";
import "../common/styles/CommonStyles.css";
import { getPathLabTestDataJson } from "./PathologyReportDataStructure";
import LaboratoryTestReportReadOnly from "./LaboratoryTestReportReadOnly";
import { PrescriptionTypes } from "../Utility/Constants";
import PrescriptionReadOnlyView from "./PrescriptionReadOnlyView";

function LaboratoryTestReport({
  serviceId,
  prescriptionId,
  onRemovePrescription,
  onSavePrescription,
}) {
  const [readOnly, setReadOnly] = useState(false);
  const [labReportData, setLabReportData] = useState(getPathLabTestDataJson());

  const { auth } = useContext(AuthContext);
  const [reportStatus, setReportStatus] = useState("Draft");
  const [currentService, setCurrentService] = useState();
  const [reportDate, setReportDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  // CHANGE 2: Add new state variables for editable fields
  const { showSnackbar } = useSnackbar();
  const [isSaving, setIsSaving] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTests, setSelectedTests] = useState(new Set());
  const [currentPrescriptionId, setCurrentPrescriptionId] =
    useState(prescriptionId);
  const [prescriptionToBeEdited, setPrescriptionToBeEdited] = useState();
  const [prescriptionStatus, setPrescriptionStatus] = useState("Draft");
  const showLoading = isLoading || showSpinner;

  // Theme and responsive breakpoints
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const displayText = (value, fallback = "Not available") => {
    return value !== undefined && value !== null ? value : fallback;
  };

  const getResultStatus = (value, lowerLimit, upperLimit) => {
    if (!value || lowerLimit === undefined || upperLimit === undefined)
      return "normal";

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "normal";

    if (numValue < lowerLimit) return "low";
    if (numValue > upperLimit) return "high";
    return "normal";
  };
  const [referenceDialog, setReferenceDialog] = useState({
    open: false,
    title: "",
    text: "",
  });

  // Add this function to handle eye icon click
  const handleReferenceClick = (testName, referenceText, event) => {
    event.stopPropagation(); // Prevent accordion from toggling
    setReferenceDialog({
      open: true,
      title: testName,
      text: referenceText,
    });
  };

  // Add this function to close the dialog
  const handleReferenceClose = () => {
    setReferenceDialog({
      open: false,
      title: "",
      text: "",
    });
  };

  // Get status chip color
  const getStatusChipColor = (status) => {
    switch (status) {
      case "high":
        return "error";
      case "low":
        return "warning";
      case "normal":
        return "success";
      default:
        return "default";
    }
  };

  useEffect(() => {
    const loadService = async () => {
      if (auth != null && serviceId != null) {
        setIsSaving(true); // Set loading state when fetching service details
        try {
          var result = await getServiceDetails(auth, serviceId);
          if (result?.isSuccess) {
            setCurrentService(result?.data);
          } else {
            // showSnackbar(result?.data, "error");
            showSnackbar(
              result?.data?.message ||
                JSON.stringify(result?.data) ||
                "An error occurred",
              "error"
            );
          }
        } catch (error) {
          showSnackbar(
            "Error loading service details: " + error.message,
            "error"
          );
        } finally {
          // Only set loading to false if we're not also loading the prescription
          if (currentPrescriptionId == null) {
            setIsSaving(false);
          }
        }
      }
    };

    const loadPrescription = async () => {
      if (auth != null && currentPrescriptionId != null) {
        setIsSaving(true);
        try {
          var result = await getPrescription(auth, currentPrescriptionId);
          if (result.isSuccess) {
            var prescription = result?.data;
            setPrescriptionToBeEdited(prescription);
            setPrescriptionStatus(prescription?.status);

            // If status is "Final", set readOnly to true
            if (prescription?.status === "Final") {
              setReadOnly(true);
            }

            var detailedPrescriptionJson = JSON.parse(
              prescription?.detailedPrescription
            );
            //console.log("detailedPrescriptionJson:", detailedPrescriptionJson);
            // In the loadPrescription function, update these lines:
            if (detailedPrescriptionJson) {
              setLabReportData(detailedPrescriptionJson);

              // setPatientId(
              //   prescription?.patientId || detailedPrescriptionJson?.patientId
              // );
              // setReferredBy(
              //   prescription?.referredBy || detailedPrescriptionJson?.referredBy
              // );
              // setReportStatusDisplay(
              //   prescription?.reportStatusDisplay ||
              //     detailedPrescriptionJson?.reportStatusDisplay
              // );
              // setBarcodeNo(
              //   prescription?.barcodeNo || detailedPrescriptionJson?.barcodeNo
              // );
              // // setSampleType(
              // //   prescription?.sampleType || detailedPrescriptionJson?.sampleType
              // // );
              // setAdditionalNotes(
              //   detailedPrescriptionJson?.additionalNotes || ""
              // ); // ADD THIS LINE
              // setSampleCollected(
              //   convertUTCToTimeZoneDateTimeString(
              //     prescription?.sampleCollectionDateTime ||
              //       detailedPrescriptionJson?.sampleCollectionDateTime
              //   )
              // );
              // setReportDate(
              //   convertUTCToTimeZoneDateTimeString(
              //     prescription?.reportDate ||
              //       detailedPrescriptionJson?.reportDate
              //   )
              // );
              // setCollectionDate(
              //   convertUTCToTimeZoneDateTimeString(
              //     prescription?.sampleCollectionDateTime ||
              //       detailedPrescriptionJson?.sampleCollectionDateTime
              //   )
              // );

              // Set lab technician and pathologist if available
              // if (
              //   prescription?.labTechnician ||
              //   detailedPrescriptionJson?.labTechnician
              // ) {
              //   setLabTechnician(
              //     prescription.labTechnician ||
              //       detailedPrescriptionJson.labTechnician
              //   );
              // }

              // if (
              //   prescription?.pathologist ||
              //   detailedPrescriptionJson?.pathologist
              // ) {
              //   setPathologist(
              //     prescription.pathologist ||
              //       detailedPrescriptionJson.pathologist
              //   );
              // }
            }
          } else {
            // showSnackbar(result?.data, "error");
            showSnackbar(
              result?.data?.message ||
                JSON.stringify(result?.data) ||
                "An error occurred",
              "error"
            );
          }
        } catch (error) {
          showSnackbar("Error loading prescription: " + error.message, "error");
        } finally {
          setIsSaving(false);
        }
      }
    };

    if (currentPrescriptionId) {
      loadPrescription();
      loadService();
    } else {
      loadService();
    }
  }, [auth, serviceId, currentPrescriptionId]);

  // 6. REPLACE onRemoveLabReportClick function
  // const saveLabReport = async (status) => {
  //   setIsSaving(true);
  //   try {
  //     var result = {};
  //     if (!prescriptionToBeEdited) {
  //       result = await addPrescription(
  //         auth,
  //         labReportData,
  //         currentService?.id,
  //         PrescriptionTypes.PathologyLabReport,
  //         status
  //       );
  //     } else {
  //       result = await updatePrescription(
  //         auth,
  //         labReportData,
  //         prescriptionToBeEdited?.id,
  //         status
  //       );
  //     }

  //     if (result.isSuccess) {
  //       onSavePrescription(serviceId, prescriptionId, result.data.id);
  //       setPrescriptionStatus(status);
  //       setReportStatus(status);

  //       // Update the local state with the saved data
  //       // setPrescriptionToBeEdited({
  //       //   ...prescriptionToBeEdited,
  //       //   status: status,
  //       //   detailedPrescription: JSON.stringify(completeLabReportData),
  //       // });
  //       setCurrentPrescriptionId(result?.data);

  //       if (status === "Final") {
  //         setReadOnly(true);
  //       }

  //       showSnackbar(
  //         "The prescription has been saved successfully.",
  //         "success"
  //       );
  //     } else {
  //       showSnackbar(
  //         result?.data?.message ||
  //           JSON.stringify(result?.data) ||
  //           "An error occurred",
  //         "error"
  //       );
  //     }
  //   } catch (error) {
  //     console.error("Error saving prescription:", error);
  //     showSnackbar("Error saving prescription: " + error.message, "error");
  //   } finally {
  //     setIsSaving(false);
  //   }
  // };
  // REPLACE the saveLabReport function with this updated version
const saveLabReport = async (status) => {
  setIsSaving(true);
  try {
    // Filter out only selected parameters and calculate their status
    const filteredTests = labReportData.tests.map(test => {
      // Filter parameters to include only selected ones
      const selectedParameters = test.parameters.filter(parameter => parameter.selected === true);
      
      // Calculate status for selected parameters
      const updatedParameters = selectedParameters.map(parameter => {
        let calculatedStatus = "Normal"; // Default status
        
        // Only calculate status if there's a result value
        if (parameter.resultValue && parameter.resultValue.trim() !== "") {
          const status = getResultStatus(parameter.resultValue, parameter.lowerLimit, parameter.upperLimit);
          // Convert to proper case format
          calculatedStatus = status === "normal" ? "Normal" : 
                           status === "low" ? "Low" : 
                           status === "high" ? "High" : "Normal";
        }
        
        return {
          ...parameter,
          status: calculatedStatus
        };
      });

      // Only include tests that have at least one selected parameter
      return selectedParameters.length > 0 ? {
        ...test,
        parameters: updatedParameters
      } : null;
    }).filter(test => test !== null); // Remove tests with no selected parameters

    const completeLabReportData = {
      ...labReportData,
      tests: filteredTests
    };

    var result = {};
    if (!prescriptionToBeEdited) {
      result = await addPrescription(
        auth,
        completeLabReportData,
        currentService?.id,
        PrescriptionTypes.PathologyLabReport,
        status
      );
    } else {
      result = await updatePrescription(
        auth,
        completeLabReportData,
        prescriptionToBeEdited?.id,
        status
      );
    }

    if (result.isSuccess) {
      onSavePrescription(serviceId, prescriptionId, result.data.id);
      setPrescriptionStatus(status);
      setReportStatus(status);

      // Update the local state with the saved data (keep all data for UI)
      // But update status for selected parameters
      const updatedLocalData = labReportData.tests.map(test => ({
        ...test,
        parameters: test.parameters.map(parameter => {
          if (parameter.selected) {
            let calculatedStatus = "Normal";
            if (parameter.resultValue && parameter.resultValue.trim() !== "") {
              const status = getResultStatus(parameter.resultValue, parameter.lowerLimit, parameter.upperLimit);
              calculatedStatus = status === "normal" ? "Normal" : 
                               status === "low" ? "Low" : 
                               status === "high" ? "High" : "Normal";
            }
            return { ...parameter, status: calculatedStatus };
          }
          return parameter;
        })
      }));

      setLabReportData({
        ...labReportData,
        tests: updatedLocalData
      });

      // Update the prescription state
      setPrescriptionToBeEdited({
        ...prescriptionToBeEdited,
        status: status,
        detailedPrescription: JSON.stringify(completeLabReportData), // This contains only selected data
      });
      setCurrentPrescriptionId(result?.data);

      if (status === "Final") {
        setReadOnly(true);
      }

      showSnackbar(
        "The prescription has been saved successfully.",
        "success"
      );
    } else {
      showSnackbar(
        result?.data?.message ||
          JSON.stringify(result?.data) ||
          "An error occurred",
        "error"
      );
    }
  } catch (error) {
    console.error("Error saving prescription:", error);
    showSnackbar("Error saving prescription: " + error.message, "error");
  } finally {
    setIsSaving(false);
  }
};

  const onRemovePrescriptionClick = async () => {
    setIsSaving(true);
    try {
      if (auth != null && prescriptionId != null) {
        var result = await removePrescription(auth, prescriptionId);
        if (result?.isSuccess) {
          onRemovePrescription(serviceId, prescriptionId);
        } else {
          // showSnackbar(result?.data, "error");
          showSnackbar(
            result?.data?.message ||
              JSON.stringify(result?.data) ||
              "An error occurred",
            "error"
          );
        }
      }
    } catch (error) {
      showSnackbar("Error removing lab report: " + error.message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  // 7. REPLACE viewLabReport function
  const viewLabReport = async () => {
    setIsSaving(true);
    try {
      if (auth != null && prescriptionId != null) {
        await viewPrescriptionById(auth, prescriptionId);
      }
    } catch (error) {
      showSnackbar("Error viewing lab report: " + error.message, "error");
    } finally {
      setIsSaving(false);
    }
  };
  if (readOnly || prescriptionStatus === "Final") {
    return (
      <PrescriptionReadOnlyView
        //serviceId={serviceId}
        prescriptionId={currentPrescriptionId}
        prescriptionType={PrescriptionTypes.PathologyLabReport}
      />
    );
  }

  const renderLabReportData = () => {
    return (
      <Grid2 container size={12} spacing={1}>
        <Grid2 container size={8}>
          <Card
            className="outerCard"
            variant="outlined"
            sx={{
              width: "100%",
              overflow: "auto",
              ml: "10px",
              padding: "10px",
            }}
          >
            <Grid2 size={12}>
              <Card
                sx={{
                  padding: "16px",
                  pointerEvents: showLoading ? "none" : "auto",
                  opacity: showLoading ? 0.6 : 1,
                  mb: 2,
                }}
                variant="outlined"
              >
                <Grid2 container size={12}>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <HeaderValueDisplay
                      header="Patient Name"
                      value={
                        currentService?.patientPrefix +
                        " " +
                        currentService?.patientFirstName +
                        " " +
                        currentService?.patientLastName
                      }
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <HeaderValueDisplay
                      header="Age and Gender"
                      value={
                        formatAge(currentService?.patientAgeInYears) +
                        " and " +
                        currentService?.patientGender
                      }
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <HeaderValueDisplay
                      header="MRN"
                      value={displayText(currentService?.patientMRN)}
                    />
                  </Grid2>
                </Grid2>
                <Grid2 container size={12}>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <HeaderValueDisplay
                      header="Referred BY"
                      value={displayText(currentService?.referredBy, "Self")}
                    />
                  </Grid2>
                </Grid2>
                <Grid2 container size={12}></Grid2>
              </Card>
            </Grid2>

            {/* Report Details */}

            {/* Report Details */}
            <Box mb={3}>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                Report Details
              </Typography>
              <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Sample Collected"
                      value={
                        labReportData.sampleCollectionDateTime
                          ? dayjs(labReportData.sampleCollectionDateTime)
                          : null
                      }
                      onChange={(newDate) =>
                        setLabReportData({
                          ...labReportData,
                          sampleCollectionDateTime: newDate
                            ? newDate.toISOString()
                            : "",
                        })
                      }
                      sx={{
                        backgroundColor: "white",
                      }}
                      format="DD-MMM-YYYY"
                      slotProps={{
                        textField: {
                          size: "small",
                          margin: "dense",
                          fullWidth: true,
                        },
                      }}
                      maxDate={dayjs()}
                    />
                  </LocalizationProvider>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Report Date"
                      value={
                        labReportData.sampleCollectionReportDate
                          ? dayjs(labReportData.sampleCollectionReportDate)
                          : null
                      }
                      onChange={(newDate) =>
                        setLabReportData({
                          ...labReportData,
                          sampleCollectionReportDate: newDate
                            ? newDate.toISOString()
                            : "",
                        })
                      }
                      format="DD-MMM-YYYY"
                      slotProps={{
                        textField: {
                          size: "small",
                          margin: "dense",
                          fullWidth: true,
                          sx: {
                            backgroundColor: "white",
                          },
                        },
                      }}
                      maxDate={dayjs()}
                    />
                  </LocalizationProvider>
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <GenericTextField
                    label="Sample Collected By"
                    value={labReportData.sampleCollectionPerformedBy || ""} // Changed 'by' to 'By'
                    onChange={(e) =>
                      setLabReportData({
                        ...labReportData,
                        sampleCollectionPerformedBy: e.target.value, // Changed 'by' to 'By'
                      })
                    }
                    fullWidth
                    placeholder="Enter person who collected sample"
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <GenericTextField
                    label="Sample Collection Location"
                    value={labReportData.sampleCollectionLocation || ""}
                    onChange={(e) =>
                      setLabReportData({
                        ...labReportData,
                        sampleCollectionLocation: e.target.value,
                      })
                    }
                    fullWidth
                    placeholder="Enter collection location"
                  />
                </Grid2>

                {/* New Sample Collection Method Autocomplete */}

                {/* <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <GenericTextField
                    label="Sample Type"
                    value={labReportData.sampleType}
                    onChange={(e) =>
                      setLabReportData({
                        ...labReportData,
                        sampleType: e.target.value,
                      })
                    }
                    fullWidth
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 6 }} pt={1}>
                 
                  <Autocomplete
                    options={sampleCollectionMethods}
                    getOptionLabel={(option) => option.value}
                    value={
                      labReportData.sampleCollectionMethod
                        ? {
                            value: labReportData.sampleCollectionMethod,
                            displayValue: labReportData.sampleCollectionMethod,
                          }
                        : null
                    }
                    onChange={(_, selectedOption) => {
                      setLabReportData({
                        ...labReportData,
                        sampleCollectionMethod: selectedOption
                          ? selectedOption.value
                          : "",
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select collection method"
                        variant="outlined"
                        size="small"
                        sx={{ background: "#fff", borderRadius: 2 }}
                      />
                    )}
                  />
                </Grid2> */}
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <Autocomplete
                    options={samples}
                    getOptionLabel={(option) => option.name}
                    value={
                      labReportData.sample
                        ? samples.find(
                            (sample) => sample.id === labReportData.sample
                          ) || null
                        : null
                    }
                    onChange={(_, selectedOption) => {
                      setLabReportData({
                        ...labReportData,
                        sample: selectedOption ? selectedOption.id : "",
                        sampleType: "", // Reset sample type when sample changes
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Sample"
                        placeholder="Select sample"
                        variant="outlined"
                        size="small"
                        sx={{ background: "#fff", borderRadius: 2 }}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <Autocomplete
                    options={sampleTypes.filter((type) =>
                      labReportData.sample
                        ? type.category ===
                          samples.find((s) => s.id === labReportData.sample)
                            ?.category
                        : true
                    )}
                    getOptionLabel={(option) => option.displayValue}
                    value={
                      labReportData.sampleType
                        ? sampleTypes.find(
                            (type) => type.value === labReportData.sampleType
                          ) || null
                        : null
                    }
                    onChange={(_, selectedOption) => {
                      setLabReportData({
                        ...labReportData,
                        sampleType: selectedOption ? selectedOption.value : "",
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Sample Type"
                        placeholder="Select sample type"
                        variant="outlined"
                        size="small"
                        sx={{ background: "#fff", borderRadius: 2 }}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 6 }}>
                  <Autocomplete
                    options={sampleCollectionMethods}
                    getOptionLabel={(option) => option.displayValue}
                    value={
                      labReportData.sampleCollectionMethod
                        ? sampleCollectionMethods.find(
                            (method) =>
                              method.value ===
                              labReportData.sampleCollectionMethod
                          ) || null
                        : null
                    }
                    onChange={(_, selectedOption) => {
                      setLabReportData({
                        ...labReportData,
                        sampleCollectionMethod: selectedOption
                          ? selectedOption.value
                          : "",
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Sample Collection Method"
                        placeholder="Select collection method"
                        variant="outlined"
                        size="small"
                        sx={{ background: "#fff", borderRadius: 2 }}
                      />
                    )}
                  />
                </Grid2>

                {/* New Sample Collected By Field */}

                {/* New Sample Collection Location Field */}
              </Grid2>
            </Box>

            {/* Test Results Section */}
            <Box mb={3}>
              <Typography variant="body1" sx={{ fontWeight: "bold", mb: 2 }}>
                Test Selection & Results
              </Typography>
            </Box>

            {labReportData?.tests?.map((test, index) => (
              <Accordion
                key={index}

                //expanded={expandedAccordions.has(test.category)}
                //onChange={handleAccordionChange(test.category)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    "& .MuiAccordionSummary-content": {
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    },
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    {test.testName}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(event) =>
                      handleReferenceClick(
                        test.testName,
                        test.referenceText,
                        event
                      )
                    }
                    sx={{ marginLeft: "auto", marginRight: 1 }}
                  >
                    <FormatLineSpacingOutlinedIcon fontSize="small" />
                  </IconButton>
                </AccordionSummary>

                <AccordionDetails>
                  {/* Responsive Table/Cards rendering */}
                  {isMobile ? (
                    // Mobile view - Cards
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {test?.parameters?.map((parameter, parameterIndex) => {
                        const parameterKey = `${parameter.name}`;
                        const status = getResultStatus(
                          parameter.resultValue,
                          parameter.lowerLimit,
                          parameter.upperLimit
                        );
                        return (
                          <Card
                            variant="outlined"
                            sx={{ p: 2 }}
                            key={parameterIndex}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                              }}
                            >
                              {/* Test Name and Select */}
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                >
                                  {parameter.parameterName}
                                </Typography>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={
                                        parameter.selected ||
                                        selectedTests.has(parameterKey)
                                      }
                                      onChange={(e) => {
                                        setLabReportData((prev) => {
                                          const updatedTests = prev?.tests?.map(
                                            (t) => {
                                              if (t.testId === test.testId) {
                                                return {
                                                  ...t,
                                                  parameters:
                                                    t?.parameters?.map((p) =>
                                                      p.parameterId ===
                                                      parameter.parameterId
                                                        ? {
                                                            ...p,
                                                            selected:
                                                              e.target.checked,
                                                          }
                                                        : p
                                                    ),
                                                };
                                              }
                                              return t;
                                            }
                                          );
                                          return {
                                            ...prev,
                                            tests: updatedTests,
                                          };
                                        });
                                      }}
                                    />
                                  }
                                  label="Select"
                                />
                              </Box>

                              {/* Result Value */}
                              <TextField
                                label="Result Value"
                                value={parameter.resultValue}
                                onChange={(e) => {
                                  setLabReportData((prev) => {
                                    const updatedTests = prev?.tests?.map(
                                      (t) => {
                                        if (t.testId === test.testId) {
                                          return {
                                            ...t,
                                            parameters: t?.parameters?.map(
                                              (p) =>
                                                p.parameterId ===
                                                parameter.parameterId
                                                  ? {
                                                      ...p,
                                                      resultValue:
                                                        e.target.value,
                                                    }
                                                  : p
                                            ),
                                          };
                                        }
                                        return t;
                                      }
                                    );
                                    return { ...prev, tests: updatedTests };
                                  });
                                }}
                                size="small"
                                fullWidth
                                placeholder="Enter result value"
                                disabled={
                                  !parameter.selected &&
                                  !selectedTests.has(parameterKey)
                                }
                              />

                              {/* Unit and Normal Range */}
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <TextField
                                  label="Unit"
                                  value={parameter.unit}
                                  size="small"
                                  sx={{ flex: 1 }}
                                  disabled
                                  InputProps={{
                                    readOnly: true,
                                  }}
                                />
                                <TextField
                                  label="Normal Range"
                                  value={`${parameter.lowerLimit} - ${parameter.upperLimit}`}
                                  size="small"
                                  sx={{ flex: 1 }}
                                  disabled
                                  InputProps={{
                                    readOnly: true,
                                  }}
                                />
                              </Box>

                              {/* Status */}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  Status:
                                </Typography>
                                <Chip
                                  label={status}
                                  color={getStatusChipColor(status)}
                                  size="small"
                                />
                              </Box>

                              {/* Comments */}
                              <TextField
                                label="Comments"
                                value={parameter.notes}
                                onChange={(e) => {
                                  setLabReportData((prev) => {
                                    const updatedTests = prev?.tests?.map(
                                      (t) => {
                                        if (t.testId === test.testId) {
                                          return {
                                            ...t,
                                            parameters: t?.parameters?.map(
                                              (p) =>
                                                p.parameterId ===
                                                parameter.parameterId
                                                  ? {
                                                      ...p,
                                                      notes: e.target.value,
                                                    }
                                                  : p
                                            ),
                                          };
                                        }
                                        return t;
                                      }
                                    );
                                    return { ...prev, tests: updatedTests };
                                  });
                                }}
                                size="small"
                                fullWidth
                                multiline
                                rows={2}
                                placeholder="Add comments"
                                disabled={
                                  !parameter.selected &&
                                  !selectedTests.has(parameterKey)
                                }
                              />
                            </Box>
                          </Card>
                        );
                      })}
                    </Box>
                  ) : (
                    // Desktop view - Table (existing code)
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Select</TableCell>
                          <TableCell>Test Name</TableCell>
                          <TableCell>Result Value</TableCell>
                          <TableCell>Unit</TableCell>
                          <TableCell>Normal Range</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Comments</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {test?.parameters?.map((parameter, parameterIndex) => {
                          const parameterKey = `${parameter.name}`;
                          const status = getResultStatus(
                            parameter.resultValue,
                            parameter.lowerLimit,
                            parameter.upperLimit
                          );
                          return (
                            <TableRow
                              key={parameterIndex}
                              sx={{
                                "&:hover": {
                                  backgroundColor: "transparent !important",
                                },
                                "&.Mui-selected": {
                                  backgroundColor: "transparent !important",
                                },
                                "&.Mui-selected:hover": {
                                  backgroundColor: "transparent !important",
                                },
                              }}
                            >
                              <TableCell>
                                <Checkbox
                                  checked={
                                    parameter.selected ||
                                    selectedTests.has(parameterKey)
                                  }
                                  onChange={(e) => {
                                    setLabReportData((prev) => {
                                      const updatedTests = prev?.tests?.map(
                                        (t) => {
                                          if (t.testId === test.testId) {
                                            return {
                                              ...t,
                                              parameters: t?.parameters?.map(
                                                (p) =>
                                                  p.parameterId ===
                                                  parameter.parameterId
                                                    ? {
                                                        ...p,
                                                        selected:
                                                          e.target.checked,
                                                      }
                                                    : p
                                              ),
                                            };
                                          }
                                          return t;
                                        }
                                      );
                                      return { ...prev, tests: updatedTests };
                                    });
                                  }}
                                />
                              </TableCell>
                              <TableCell>{parameter.parameterName}</TableCell>
                              <TableCell>
                                <TextField
                                  value={parameter.resultValue}
                                  onChange={(e) => {
                                    setLabReportData((prev) => {
                                      const updatedTests = prev?.tests?.map(
                                        (t) => {
                                          if (t.testId === test.testId) {
                                            return {
                                              ...t,
                                              parameters: t?.parameters?.map(
                                                (p) =>
                                                  p.parameterId ===
                                                  parameter.parameterId
                                                    ? {
                                                        ...p,
                                                        resultValue:
                                                          e.target.value,
                                                      }
                                                    : p
                                              ),
                                            };
                                          }
                                          return t;
                                        }
                                      );
                                      return { ...prev, tests: updatedTests };
                                    });
                                  }}
                                  size="small"
                                  fullWidth
                                  placeholder="Enter result value"
                                  disabled={
                                    !parameter.selected &&
                                    !selectedTests.has(parameterKey)
                                  }
                                />
                              </TableCell>
                              <TableCell>{parameter.unit}</TableCell>
                              <TableCell>
                                {parameter.lowerLimit} - {parameter.upperLimit}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={status}
                                  color={getStatusChipColor(status)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  value={parameter.notes}
                                  onChange={(e) => {
                                    setLabReportData((prev) => {
                                      const updatedTests = prev?.tests?.map(
                                        (t) => {
                                          if (t.testId === test.testId) {
                                            return {
                                              ...t,
                                              parameters: t?.parameters?.map(
                                                (p) =>
                                                  p.parameterId ===
                                                  parameter.parameterId
                                                    ? {
                                                        ...p,
                                                        notes: e.target.value,
                                                      }
                                                    : p
                                              ),
                                            };
                                          }
                                          return t;
                                        }
                                      );
                                      return { ...prev, tests: updatedTests };
                                    });
                                  }}
                                  size="small"
                                  fullWidth
                                  multiline
                                  rows={2}
                                  placeholder="Add comments"
                                  disabled={
                                    !parameter.selected &&
                                    !selectedTests.has(parameterKey)
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Card>
        </Grid2>
        <Grid2 container size={4}>
          <Card
            className="outerCard"
            variant="outlined"
            sx={{
              width: "100%",
              overflow: "auto",
              ml: "10px",
              padding: "10px",
            }}
          >
            {/* Additional Notes */}
            {/* <Grid2 size={12}>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                Additional Notes
              </Typography>
              <TextField
                label="Additional Notes"
                multiline
                rows={4}
                value={labReportData.additionalNotes}
                onChange={(e) => {
                  console.log("Additional notes changed:", e.target.value);
                  setLabReportData({
                    ...labReportData,
                    additionalNotes: e.target.value,
                  });
                }}
                placeholder="Enter any additional observations or recommendations..."
                fullWidth
              />
            </Grid2> */}

            {/* Summary and Interpretation */}
            <Grid2 size={12}>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                Summary & Interpretation
              </Typography>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Box mb={2}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Abnormal Results Summary
                  </Typography>
                  {(() => {
                    const abnormalResults = [];
                    labReportData?.tests?.forEach((test) => {
                      (test.parameters || [])?.forEach((parameter) => {
                        // Only include selected parameters
                        if (
                          parameter.selected ||
                          selectedTests.has(`${parameter.name}`)
                        ) {
                          const status = getResultStatus(
                            parameter.resultValue,
                            parameter.lowerLimit,
                            parameter.upperLimit
                          );
                          // Only include if status is not normal (i.e., low or high)
                          if (status !== "normal") {
                            abnormalResults.push({
                              testName: parameter.parameterName,
                              value: parameter.resultValue,
                              unit: parameter.unit,
                              status: status,
                              normalRange: `${parameter.lowerLimit} - ${parameter.upperLimit}`,
                              notes: parameter.notes,
                            });
                          }
                        }
                      });
                    });

                    return abnormalResults.length > 0 ? (
                      <ul>
                        {abnormalResults?.map((result, index) => (
                          <li key={index}>
                            <Typography variant="body2">
                              <strong>{result.testName}</strong>: {result.value}{" "}
                              {result.unit} ({result.status.toUpperCase()}) -
                              Normal: {result.normalRange}
                              {result.notes && (
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  sx={{ ml: 2, fontStyle: "italic" }}
                                >
                                  Note: {result.notes}
                                </Typography>
                              )}
                            </Typography>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No abnormal results to display.
                      </Typography>
                    );
                  })()}
                </Box>
                <Divider />
                <Box mt={2}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Pathologist's Comments
                  </Typography>
                  <TextField
                    multiline
                    rows={3}
                    value={labReportData.additionalNotes}
                    onChange={(e) => {
                      console.log(
                        "Pathologist comments changed:",
                        e.target.value
                      );
                      setLabReportData({
                        ...labReportData,
                        additionalNotes: e.target.value,
                      });
                    }}
                    placeholder="Enter your clinical interpretation and recommendations..."
                    fullWidth
                  />
                </Box>
              </Card>
            </Grid2>
          </Card>
        </Grid2>
      </Grid2>
    );
  };

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      {/* Loading Overlay */}
      {isSaving && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(216, 214, 214, 0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1301,
          }}
        >
          {showLoadingImage()}
        </div>
      )}

      <Grid2 container size={12}>
        <Grid2 container size={12}>
          <Grid2 size={12}>
            <MenuBar
              showSave={reportStatus !== "Final"}
              onSave={() => saveLabReport("Draft")}
              showFinalize={reportStatus !== "Final"}
              onFinalize={() => saveLabReport("Final")}
              showDelete={reportStatus === "Draft"}
              onDelete={onRemovePrescriptionClick}
              showPrint={reportStatus === "Final"}
              onPrint={viewLabReport}
              disabled={isSaving}
            />
          </Grid2>
        </Grid2>

        <Grid2
          container
          size={12}
          spacing={1}
          sx={{
            overflow: "auto",
            maxHeight: "80vh",
          }}
        >
          <Grid2
            size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
            sx={{ paddingTop: "10px" }}
          >
            <Box
              sx={{
                opacity: isSaving ? 0.7 : 1,
                transition: "opacity 0.3s ease",
              }}
            >
              {renderLabReportData()}
            </Box>
          </Grid2>
        </Grid2>
      </Grid2>
      <Dialog
        open={referenceDialog.open}
        onClose={handleReferenceClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Reference Text - {referenceDialog.title}</DialogTitle>
        <DialogContent>
          <div
            style={{
              whiteSpace: "pre-wrap",
              fontFamily: theme.typography.body1.fontFamily,
              fontSize: theme.typography.body1.fontSize,
              lineHeight: theme.typography.body1.lineHeight,
            }}
            dangerouslySetInnerHTML={{
              __html:
                referenceDialog.text ||
                "No reference text available for this test.",
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReferenceClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LaboratoryTestReport;
